<template>
  <div class="max-w-360 m-auto w-full h-full flex flex-col justify-center items-center">
    <div v-if="currentDevice && layout" class="relative flex-1 flex items-center justify-center">
      <MouseLayoutPanel :key-buttons="dpiButtons" :key-urls="dpiUrls" :img="imgUrl">
        <template #key-label="{ mouseKey }">
          {{ getKeyLabel(mouseKey) }}
        </template>
      </MouseLayoutPanel>
    </div>
    <div class="mb-2 flex flex-col gap-4.5 h-67.5 w-335">
      <DPISection />
    </div>
  </div>
</template>

<script setup lang="ts">
  import { computed } from 'vue'
  import { storeToRefs } from 'pinia'
  import type { LayoutKey } from '@rapoo/device-sdk'
  import MouseLayoutPanel from '../keys/components/MouseLayoutPanel.vue'
  import { useDeviceStore } from '@/stores'
  import { getImageUrl } from '@/utils/assets'
  import { useMouseKeyMappings } from '../composables/useMouseKeyMappings'
  import { useKeyLabel } from '../keys/composables/useKeyLabel'
  import DPISection from './components/DPISection.vue'

  const deviceStore = useDeviceStore()
  const { currentDevice } = storeToRefs(deviceStore)
  const layout = computed(() => currentDevice.value?.manifest.layout)

  const { keySettings } = useMouseKeyMappings()
  const { getKeyLabel } = useKeyLabel(keySettings)

  /**
   * 计算物理按键经过改键映射后的实际功能 keywords。
   * - 未改键：返回按键自身的默认 keywords
   * - 改成 code 映射类功能（含 DPI 相关）：返回 target.keywords
   * - 改成宏/组合键等无可读 keywords 的功能：返回空串（不会被识别为 DPI）
   */
  const getEffectiveKeyword = (key: LayoutKey): string => {
    const setting = keySettings.value.find((s) => s.keywords === key.keywords && s.isModified)
    if (!setting) return key.keywords

    const target = setting.target
    if (target && typeof target === 'object' && 'keywords' in target) {
      return (target as { keywords: string }).keywords
    }
    return ''
  }

  const dpiButtons = computed<LayoutKey[]>(() => {
    const keys = layout.value?.keys[0] || []
    return keys.filter((key) => getEffectiveKeyword(key).startsWith('DPI'))
  })

  const dpiUrls = computed(() => {
    return dpiButtons.value.map((key) => {
      const ui = currentDevice.value?.manifest?.ui
      if (!ui?.assetsDir) return ''
      return getImageUrl(`${ui.assetsDir}/${key.url}.svg`)
    })
  })

  const currentDeviceColor = computed(() => {
    if (!currentDevice.value?.manifest?.ui.colors) return

    const { series, current } = currentDevice.value?.manifest?.ui.colors
    if (!series.length) return

    return series.find((color) => color.key === current)
  })

  const imgUrl = computed(() => {
    const ui = currentDevice.value?.manifest?.ui
    const color = currentDeviceColor.value
    if (!ui?.assetsDir || !color?.key) return ''
    return getImageUrl(`${ui.assetsDir}/func_${color.key}.png`)
  })
</script>
